"use client";

import { useMemo, useState } from "react";
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Layers,
  LineChart,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Label } from "@/shadcn-components/ui/label";
import { Textarea } from "@/shadcn-components/ui/textarea";
import { Badge } from "@/shadcn-components/ui/badge";
import { cn } from "@/lib/utils";
import type { CreateProjectDraft } from "@/lib/custom-indicators/create-draft";
import {
  DEFAULT_TEMPLATE_ID,
  INDICATOR_TEMPLATES,
  type IndicatorTemplate,
} from "@/lib/custom-indicators/templates";
import type { CustomIndicatorProjectKind } from "@/lib/custom-indicators/types";

const STEPS = [
  { id: 1, label: "Template" },
  { id: 2, label: "Details" },
  { id: 3, label: "Review" },
] as const;

const TEMPLATE_ICONS: Record<string, typeof Sparkles> = {
  "trend-dashboard": Sparkles,
  "minimal-ma": LineChart,
  "rsi-oscillator": BarChart3,
  "macd-histogram": BarChart3,
  "zero-line-macd": BarChart3,
  "bollinger-bands": Layers,
  "buy-sell-signals": TrendingUp,
};

interface StudioCreateWizardProps {
  onCancel: () => void;
  onComplete: (draft: CreateProjectDraft) => void;
}

export default function StudioCreateWizard({
  onCancel,
  onComplete,
}: StudioCreateWizardProps) {
  const [step, setStep] = useState(1);
  const [templateId, setTemplateId] = useState(DEFAULT_TEMPLATE_ID);
  const [name, setName] = useState("My Custom Indicator");
  const [version, setVersion] = useState("1.0");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState<CustomIndicatorProjectKind>("indicator");

  const selectedTemplate = useMemo(
    () => INDICATOR_TEMPLATES.find((t) => t.id === templateId),
    [templateId]
  );

  const canNext = useMemo(() => {
    if (step === 1) return Boolean(templateId);
    if (step === 2) return name.trim().length > 0;
    return true;
  }, [step, templateId, name]);

  const handleCreate = () => {
    if (!selectedTemplate) return;
    const draft: CreateProjectDraft = {
      name: name.trim() || selectedTemplate.name,
      version: version.trim() || "1.0",
      description: description.trim(),
      templateId,
      overlay: selectedTemplate.overlay,
      kind: "indicator",
    };
    onComplete(draft);
  };

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <div className="shrink-0 border-b border-border px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold">New custom indicator</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Step {step} of {STEPS.length} — {STEPS[step - 1]?.label}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {STEPS.map((s) => (
              <div
                key={s.id}
                className={cn(
                  "h-1.5 w-8 rounded-full transition-colors",
                  s.id <= step ? "bg-primary" : "bg-muted"
                )}
                aria-hidden
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {step === 1 ? (
          <TemplateStep
            selectedId={templateId}
            onSelect={setTemplateId}
          />
        ) : null}
        {step === 2 ? (
          <DetailsStep
            name={name}
            version={version}
            description={description}
            onNameChange={setName}
            onVersionChange={setVersion}
            onDescriptionChange={setDescription}
            template={selectedTemplate}
          />
        ) : null}
        {step === 3 ? (
          <ReviewStep
            template={selectedTemplate}
            name={name}
            version={version}
            description={description}
            kind={kind}
            onKindChange={setKind}
          />
        ) : null}
      </div>

      <div className="shrink-0 flex items-center gap-2 border-t border-border px-4 py-3">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <div className="flex-1" />
        {step > 1 ? (
          <Button
            variant="outline"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
        ) : null}
        {step < 3 ? (
          <Button
            disabled={!canNext}
            onClick={() => setStep((s) => Math.min(3, s + 1))}
            className="gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button disabled={!canNext || kind !== "indicator"} onClick={handleCreate}>
            Create & open editor
          </Button>
        )}
      </div>
    </div>
  );
}

function TemplateStep({
  selectedId,
  onSelect,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
      {INDICATOR_TEMPLATES.map((t) => (
        <TemplateCard
          key={t.id}
          template={t}
          selected={selectedId === t.id}
          onSelect={() => onSelect(t.id)}
        />
      ))}
    </div>
  );
}

function TemplateCard({
  template,
  selected,
  onSelect,
}: {
  template: IndicatorTemplate;
  selected: boolean;
  onSelect: () => void;
}) {
  const Icon = TEMPLATE_ICONS[template.id] ?? Sparkles;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-colors",
        selected
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : "border-border bg-card/40 hover:bg-muted/50"
      )}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <div
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-md",
            selected ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <Badge variant="secondary" className="text-[10px] shrink-0">
          {template.overlay ? "Overlay" : "Pane"}
        </Badge>
      </div>
      <span className="text-sm font-medium">{template.name}</span>
      <p className="text-xs text-muted-foreground line-clamp-2">
        {template.description}
      </p>
    </button>
  );
}

function DetailsStep({
  name,
  version,
  description,
  onNameChange,
  onVersionChange,
  onDescriptionChange,
  template,
}: {
  name: string;
  version: string;
  description: string;
  onNameChange: (v: string) => void;
  onVersionChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  template?: IndicatorTemplate;
}) {
  return (
    <div className="max-w-lg mx-auto w-full space-y-4">
      {template ? (
        <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Base template: <strong className="text-foreground">{template.name}</strong>
          {" · "}
          {template.overlay ? "Overlay on chart" : "Separate pane"}
        </div>
      ) : null}
      <div className="space-y-2">
        <Label htmlFor="wiz-name">Project name</Label>
        <Input
          id="wiz-name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="My Custom Indicator"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="wiz-version">Version</Label>
        <Input
          id="wiz-version"
          value={version}
          onChange={(e) => onVersionChange(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="wiz-desc">Description (optional)</Label>
        <Textarea
          id="wiz-desc"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          rows={3}
          placeholder="What does this indicator do?"
        />
      </div>
    </div>
  );
}

function ReviewStep({
  template,
  name,
  version,
  description,
  kind,
  onKindChange,
}: {
  template?: IndicatorTemplate;
  name: string;
  version: string;
  description: string;
  kind: CustomIndicatorProjectKind;
  onKindChange: (k: CustomIndicatorProjectKind) => void;
}) {
  return (
    <div className="max-w-lg mx-auto w-full space-y-5">
      <section className="rounded-lg border border-border divide-y divide-border">
        <ReviewRow label="Template" value={template?.name ?? "—"} />
        <ReviewRow
          label="Placement"
          value={template?.overlay ? "Overlay on price" : "Separate pane"}
        />
        <ReviewRow label="Name" value={name.trim() || "—"} />
        <ReviewRow label="Version" value={version.trim() || "1.0"} />
        {description.trim() ? (
          <ReviewRow label="Description" value={description.trim()} />
        ) : null}
      </section>

      <section className="space-y-3">
        <Label className="text-sm">Project type</Label>
        <div className="grid gap-2">
          <button
            type="button"
            onClick={() => onKindChange("indicator")}
            className={cn(
              "rounded-lg border px-3 py-3 text-left transition-colors",
              kind === "indicator"
                ? "border-primary bg-primary/5"
                : "border-border hover:bg-muted/50"
            )}
          >
            <span className="text-sm font-medium">Indicator</span>
            <p className="text-xs text-muted-foreground mt-0.5">
              Chart technical indicator (recommended)
            </p>
          </button>
          <div
            className="rounded-lg border border-dashed border-border px-3 py-3 opacity-60 cursor-not-allowed"
            aria-disabled
          >
            <span className="text-sm font-medium text-muted-foreground">
              Trading algo
            </span>
            <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
              Coming soon — automated trading strategies will be available in a
              future release.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 px-3 py-2.5 text-sm">
      <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
      <span className="flex-1 min-w-0 break-words">{value}</span>
    </div>
  );
}
